import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FInputRadioGroup from 'components/BaseComponents/FInputRadioGroup'
import SBox from 'components/opinionless/SBox'
import SText from 'components/opinionless/SText'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { SStyled, useSTheme } from 'styles/theme'
import { useAddApplicationAnswersMutation } from 'types/graphql'
import { ga4_useTitle } from 'utils/ga4'
import XBanner from 'v2/components/XBanner'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import ConditionalRender from 'utils/conditionalRender'

const LBTextHeader = SStyled(SText)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`

interface Props {
    setCurrentStep?: (number) => void
    setSelectedDocument: any
    fontFamily: any
    nextStep?: any
    onPrevious?: any
    updatePercentage: any
    disableBackButton?: boolean
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const DocumentsProofOfAddress: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const form = useForm()
    const { t } = useTranslation(window.LANG_NS)
    const [addApplicationAnswers] = useAddApplicationAnswersMutation()
    const applicationContext = useContext(ApplicationContext)

    ga4_useTitle('Household income')

    const { refetch: refetchApplicationAnswers } = useApplicationAnswers()

    const onSubmit = async (option) => {
        addApplicationAnswers({
            variables: {
                answers: [
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'PROOF_OF_RESIDENCY_CATEGORY',
                        response: option.document_category,
                    },
                ],
            },
        }).then(() => {
            props.setSelectedDocument(option.document_category)
            refetchApplicationAnswers()
        })
        props.nextStep()
    }

    const fontFamily = props.fontFamily

    useEffect(() => {
        if (
            applicationContext?.answers?.length &&
            applicationContext?.answers.find((itm) => itm.category === 'PROOF_OF_RESIDENCY_CATEGORY')
        ) {
            form.setValue(
                'document_category',
                applicationContext?.answers.find((itm) => itm.category === 'PROOF_OF_RESIDENCY_CATEGORY').response
            )
        }
    }, [applicationContext])

    const radioDataArray = useMemo(() => {
        return [
            {
                text: t('long_beach_valid_drivers_license'),
                id: 'long_beach_valid_drivers_license',
            },
            {
                text: t('long_beach_one_paystub_within_3_months_show_address'),
                id: 'long_beach_one_paystub_within_3_months_show_address',
            },
            {
                text: t('long_beach_eligibility_proof_government_at_90813_address'),
                id: 'long_beach_eligibility_proof_government_at_90813_address',
            },
            {
                text: t('long_beach_letter_nonprofit_90813_last_3_months'),
                id: 'long_beach_letter_nonprofit_90813_last_3_months',
            },
            {
                text: t('long_beach_utility_bill_last_months_with_address'),
                id: 'long_beach_utility_bill_last_months_with_address',
            },
            {
                text: t('long_beach_rental_lease_agreement'),
                id: 'long_beach_rental_lease_agreement',
            },
            {
                text: t('long_beach_piece_of_mail_that_show_address'),
                id: 'long_beach_piece_of_mail_that_show_address',
            },
        ]
    }, [t])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.documents,
        WIZARD_STEPS_MAPPING.documents.proofOfAddressChoice,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    return (
        <FormProvider {...form}>
            <Container>
                <SBox as="form" onSubmit={form.handleSubmit(onSubmit)}>
                    <ConditionalRender if={!props.disableBackButton}>
                        <FButtonPrevious onClick={props.onPrevious} />
                    </ConditionalRender>

                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.secondaryColor,
                            fontFamily: fontFamily.header,
                        }}
                    >
                        {t('long_beach_documents_poa_header')}
                    </LBTextHeader>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '800',
                            lineHeight: '140%',
                            color: theme.colors.black,
                            fontSize: '16px',
                        }}
                    >
                        {t('long_beach_documents_poa_subheader')}
                    </LBText>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                            marginBottom: '24px',
                        }}
                    >
                        {t('long_beach_documents_poa_info_description')}
                    </LBText>
                    <div style={{ marginTop: '24px' }}>
                        <XBanner
                            title={t('long_beach_documents_poa_warning_title')}
                            description={t('long_beach_documents_poa_warning_description')}
                            isButton={false}
                            type="warning"
                        />
                    </div>

                    <FInputRadioGroup
                        radioDataArray={radioDataArray}
                        name={'document_category'}
                        id={'document_category'}
                        minimumSelection={1}
                    />

                    <LBButton
                        type={'submit'}
                        id={'continueButton'}
                        title={t('save_and_continue')}
                        width={'100%'}
                        mt={7}
                        mb={5}
                        textStyle={{
                            fontWeight: 700,
                            fontSize: '14px',
                            lineHeight: '140%',
                            fontFamily: fontFamily.body,
                            color: theme.colors.white,
                        }}
                    >
                        {t('save_and_continue')}
                    </LBButton>
                </SBox>
            </Container>
        </FormProvider>
    )
}

export default DocumentsProofOfAddress
